import { useEffect, useMemo, useState } from "react";

const getFieldStatus = (field) => {
  if (field?.user_overridden) return "user";
  if (field?.needs_review) return "review";
  if (field?.answer != null && field.answer.toString().trim() !== "") {
    return "answered";
  }
  return "unanswered";
};

/**
 * Owns the read/derived state powering the field navigator (left sidebar)
 * and the page thumbnail strip (right sidebar):
 *   - searchQuery, collapsedSections, hoveredFieldId, highlightedFieldId
 *   - sidebarSections (grouped + filtered by search)
 *   - stats (answered / user / review / unanswered counts)
 *   - pageStats (per-page totals)
 *   - fieldsByPage (used by the PDF body to render bbox overlays)
 *   - currentPage (driven by an IntersectionObserver on the page wrappers)
 *   - handleFieldRowClick (scrolls + briefly highlights a bbox)
 *   - goToPage (smooth-scrolls to a page)
 *   - toggleSection
 */
export default function useFieldsView({ job, totalPages, pageWrapperRefs }) {
  const [searchQuery, setSearchQuery] = useState("");
  const [hoveredFieldId, setHoveredFieldId] = useState(null);
  const [highlightedFieldId, setHighlightedFieldId] = useState(null);
  const [collapsedSections, setCollapsedSections] = useState(() => new Set());
  const [currentPage, setCurrentPage] = useState(0);
  // Status filter: 'all' | 'filled' | 'review' | 'empty'.
  const [statusFilter, setStatusFilter] = useState("all");

  const fieldsByPage = useMemo(() => {
    const grouped = {};
    (job?.fields || []).forEach((f) => {
      if (typeof f.page !== "number") return;
      if (!grouped[f.page]) grouped[f.page] = [];
      grouped[f.page].push(f);
    });
    return grouped;
  }, [job]);

  const sidebarSections = useMemo(() => {
    const fields = job?.fields || [];
    const q = searchQuery.trim().toLowerCase();
    const matchStatus = (f) => {
      if (statusFilter === "all") return true;
      const s = getFieldStatus(f);
      if (statusFilter === "filled") return s === "answered" || s === "user";
      if (statusFilter === "review") return s === "review";
      if (statusFilter === "empty") return s === "unanswered";
      return true;
    };
    const filtered = fields.filter((f) => {
      if (!matchStatus(f)) return false;
      if (!q) return true;
      const inLabel = (f.label || "").toLowerCase().includes(q);
      const inSection = (f.section || "").toLowerCase().includes(q);
      const inFieldId = (f.field_id || "").toLowerCase().includes(q);
      const inAnswer = (f.answer ?? "").toString().toLowerCase().includes(q);
      return inLabel || inSection || inFieldId || inAnswer;
    });

    // Stable section order: by first occurrence in the original fields list.
    const order = [];
    const grouped = {};
    for (const f of filtered) {
      const section = f.section || "Other";
      if (!grouped[section]) {
        grouped[section] = [];
        order.push(section);
      }
      grouped[section].push(f);
    }
    return order.map((section) => [section, grouped[section]]);
  }, [job, searchQuery, statusFilter]);

  const stats = useMemo(() => {
    const fields = job?.fields || [];
    let answered = 0;
    let user = 0;
    let review = 0;
    let unanswered = 0;
    for (const f of fields) {
      const s = getFieldStatus(f);
      if (s === "answered") answered++;
      else if (s === "user") user++;
      else if (s === "review") review++;
      else unanswered++;
    }
    return {
      total: fields.length,
      answered,
      user,
      review,
      unanswered,
      done: answered + user,
    };
  }, [job]);

  const pageStats = useMemo(() => {
    const map = {};
    for (let i = 0; i < totalPages; i++) {
      map[i] = { total: 0, answered: 0, review: 0, unanswered: 0 };
    }
    for (const f of job?.fields || []) {
      if (typeof f.page !== "number") continue;
      if (!map[f.page]) {
        map[f.page] = { total: 0, answered: 0, review: 0, unanswered: 0 };
      }
      map[f.page].total += 1;
      const s = getFieldStatus(f);
      if (s === "answered" || s === "user") map[f.page].answered += 1;
      else if (s === "review") map[f.page].review += 1;
      else map[f.page].unanswered += 1;
    }
    return map;
  }, [job, totalPages]);

  // Track which page is currently visible by observing each page wrapper.
  useEffect(() => {
    if (!totalPages) return;
    const observer = new IntersectionObserver(
      (entries) => {
        let bestIdx = null;
        let bestRatio = 0;
        for (const entry of entries) {
          const idx = Number(entry.target.dataset.pageIndex);
          if (Number.isNaN(idx)) continue;
          if (entry.intersectionRatio > bestRatio) {
            bestRatio = entry.intersectionRatio;
            bestIdx = idx;
          }
        }
        if (bestIdx != null && bestRatio > 0) {
          setCurrentPage((prev) => (prev === bestIdx ? prev : bestIdx));
        }
      },
      { threshold: [0, 0.25, 0.5, 0.75, 1] }
    );
    for (let i = 0; i < totalPages; i++) {
      const el = pageWrapperRefs.current[i];
      if (el) {
        el.dataset.pageIndex = String(i);
        observer.observe(el);
      }
    }
    return () => observer.disconnect();
  }, [totalPages, pageWrapperRefs]);

  const toggleSection = (section) => {
    setCollapsedSections((prev) => {
      const next = new Set(prev);
      if (next.has(section)) next.delete(section);
      else next.add(section);
      return next;
    });
  };

  const handleFieldRowClick = (field) => {
    setHighlightedFieldId(field.id);
    const pageEl = pageWrapperRefs.current[field.page];
    if (pageEl && typeof pageEl.scrollIntoView === "function") {
      pageEl.scrollIntoView({ behavior: "smooth", block: "start" });
    }
    // Auto-clear the highlight after a couple of seconds.
    setTimeout(() => {
      setHighlightedFieldId((curr) => (curr === field.id ? null : curr));
    }, 2500);
  };

  const goToPage = (pageIndex) => {
    const el = pageWrapperRefs.current[pageIndex];
    if (el && typeof el.scrollIntoView === "function") {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return {
    // search + collapse + hover/highlight state
    searchQuery,
    setSearchQuery,
    statusFilter,
    setStatusFilter,
    collapsedSections,
    toggleSection,
    hoveredFieldId,
    setHoveredFieldId,
    highlightedFieldId,
    // memoized derived data
    fieldsByPage,
    sidebarSections,
    stats,
    pageStats,
    // page navigation
    currentPage,
    handleFieldRowClick,
    goToPage,
    // helper exposed for the bbox overlay
    getFieldStatus,
  };
}
